/**
 * Nexus Dashboard Page
 * Overview of key metrics and system health
 */

import { useEffect } from 'react'
import { useNexusStore } from '@/store/nexusStore'

function StatCard({ 
  title, 
  value, 
  subtitle, 
  icon, 
  trend 
}: { 
  title: string
  value: string | number
  subtitle?: string
  icon: string
  trend?: { value: number; label: string }
}) {
  return (
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-400">{title}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
          {trend && (
            <p className={`text-sm mt-2 ${trend.value >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value)} {trend.label}
            </p>
          )}
        </div>
        <span className="text-3xl">{icon}</span>
      </div>
    </div>
  )
}


export default function NexusDashboard() {
  const { overview, overviewLoading, fetchOverview } = useNexusStore()

  useEffect(() => {
    fetchOverview()
  }, [fetchOverview])

  if (overviewLoading && !overview) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-700 rounded w-48" />
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-slate-800 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400 mt-1">
          Overview of Niki system health and metrics
        </p>
      </div>

      {/* User Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Users"
          value={overview?.users.total_users || 0}
          icon="👥"
          trend={{ value: overview?.users.new_this_week || 0, label: 'this week' }}
        />
        <StatCard
          title="Active (24h)"
          value={overview?.users.active_24h || 0}
          subtitle={`${overview?.users.active_7d || 0} in 7 days`}
          icon="🟢"
        />
        <StatCard
          title="Total Wallet Balance"
          value={`KES ${(overview?.wallets.total_balance || 0).toLocaleString()}`}
          subtitle={`Avg: KES ${(overview?.wallets.avg_balance || 0).toFixed(0)}`}
          icon="💰"
        />
        <StatCard
          title="Revenue (Month)"
          value={`KES ${(overview?.revenue.total_month || 0).toLocaleString()}`}
          subtitle={`Today: KES ${(overview?.revenue.total_today || 0).toFixed(0)}`}
          icon="📈"
        />
      </div>

      {/* API Health & Abilities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* API Health */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-lg font-semibold text-white mb-4">API Services Health</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                <span className="text-xl">✓</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{overview?.api_health.healthy || 0}</p>
                <p className="text-sm text-gray-400">Healthy</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <span className="text-xl">⚠</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{overview?.api_health.degraded || 0}</p>
                <p className="text-sm text-gray-400">Degraded</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
                <span className="text-xl">✗</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{overview?.api_health.down || 0}</p>
                <p className="text-sm text-gray-400">Down</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <span className="text-xl">💸</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{overview?.api_health.low_balance_alerts || 0}</p>
                <p className="text-sm text-gray-400">Low Balance</p>
              </div>
            </div>
          </div>
        </div>

        {/* Abilities Summary */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-lg font-semibold text-white mb-4">Abilities Status</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                <span className="text-xl">⚡</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{overview?.abilities.enabled || 0}</p>
                <p className="text-sm text-gray-400">Enabled</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gray-500/20 flex items-center justify-center">
                <span className="text-xl">○</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{overview?.abilities.disabled || 0}</p>
                <p className="text-sm text-gray-400">Disabled</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <span className="text-xl">⚠</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{overview?.abilities.with_issues || 0}</p>
                <p className="text-sm text-gray-400">With Issues</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <span className="text-xl">🔧</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{overview?.abilities.in_maintenance || 0}</p>
                <p className="text-sm text-gray-400">Maintenance</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wallet Activity */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h2 className="text-lg font-semibold text-white mb-4">Today's Wallet Activity</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-gray-400">Deposits</p>
            <p className="text-2xl font-bold text-green-400 mt-1">
              +KES {(overview?.wallets.total_deposits_today || 0).toLocaleString()}
            </p>
          </div>
          <div className="text-center p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-gray-400">Debits (Spend)</p>
            <p className="text-2xl font-bold text-red-400 mt-1">
              -KES {Math.abs(overview?.wallets.total_debits_today || 0).toLocaleString()}
            </p>
          </div>
          <div className="text-center p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-gray-400">Net Flow</p>
            <p className={`text-2xl font-bold mt-1 ${
              (overview?.wallets.total_deposits_today || 0) + (overview?.wallets.total_debits_today || 0) >= 0
                ? 'text-green-400'
                : 'text-red-400'
            }`}>
              KES {((overview?.wallets.total_deposits_today || 0) + (overview?.wallets.total_debits_today || 0)).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Last Updated */}
      {overview?.generated_at && (
        <p className="text-sm text-gray-500 mt-6 text-right">
          Last updated: {new Date(overview.generated_at).toLocaleString()}
        </p>
      )}
    </div>
  )
}
